import {
  combineLatestWith,
  distinctUntilChanged,
  merge,
  type Observable,
  take,
  tap,
} from 'rxjs'
import type { Member } from '@/features/iam/members/Member'
import type { UserData } from '@/features/iam/users/UserData'
import type { FetchHubspotTokenFn } from '@/features/freshbooks'
import { filterNonNil } from '@/utils/rx-util'
import { createTrackingService } from './TrackingService'
import { hubspotIntegration } from './hubspot'

/**
 * Options for {@link TrackingFeature}.
 */
export interface TrackingFeatureOptions {
  token$: Observable<string>
  currentMember$: Observable<Member>
  currentUserData$: Observable<UserData>
  logout$: Observable<void>
  fetchHubspotToken: FetchHubspotTokenFn
}

/**
 * Session recording, analytics and the HubSpot chat widget.
 */
export type TrackingFeature = ReturnType<typeof TrackingFeature>

export function TrackingFeature(options: TrackingFeatureOptions) {
  const trackingService = createTrackingService()

  const identify$ = options.currentMember$.pipe(
    filterNonNil(),
    distinctUntilChanged(),
    combineLatestWith(options.currentUserData$),
    take(1),
    tap(([member, userData]) => trackingService.identify(member, userData)),
  )

  const reset$ = options.logout$.pipe(
    take(1),
    tap(() => trackingService.reset()),
  )

  merge(
    identify$,
    reset$,
    hubspotIntegration(
      options.currentMember$,
      options.fetchHubspotToken,
      options.token$,
      options.currentUserData$,
    ),
  ).subscribe()

  return {
    // Public
    trackingService,
  }
}
